import { BlogNewsListItemProp, getBlogNewsPostsForList } from '@/services';
import { SITEMAP_PRIORITIES, ContentCategoryId } from '@/constants';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '@/constants/language';
import { MetadataRoute } from 'next';
import { isSupportedLanguage } from '@/utils/language';
import { generateLocalizedUrl } from '@/constants/links';

type LanguageCategoryCount = Record<SupportedLanguage, Record<string, number>>;

interface GeneratePagesProps {
  posts: BlogNewsListItemProp[];
  path: string;
  baseUrl: string;
}

interface GenerateCategoryPaginationSitemapProps {
  path: string;
  categoryPostCounts: LanguageCategoryCount;
  baseUrl: string;
  postsPerPage?: number;
}

// Category generator

const aggregateCategoryPostCounts = (
  blogPosts: BlogNewsListItemProp[],
): LanguageCategoryCount => {
  const initialCount: LanguageCategoryCount = {
    ko: { all: 0 },
    en: { all: 0 },
  };

  return blogPosts.reduce((acc, post) => {
    if (post.categoryKo?.length) {
      acc.ko.all++;

      post.categoryKo.forEach(category => {
        acc.ko[category] = (acc.ko[category] || 0) + 1;
      });
    }

    if (post.categoryEn?.length) {
      acc.en.all++;

      post.categoryEn.forEach(category => {
        acc.en[category] = (acc.en[category] || 0) + 1;
      });
    }
    return acc;
  }, initialCount);
};

export const generateCategoryPaginationSitemap = ({
  categoryPostCounts,
  baseUrl,
  path,
  postsPerPage,
}: GenerateCategoryPaginationSitemapProps): MetadataRoute.Sitemap => {
  const sitemap: MetadataRoute.Sitemap = [];

  SUPPORTED_LANGUAGES.forEach(language => {
    const categoriesForLanguage = categoryPostCounts[language];

    Object.entries(categoriesForLanguage).forEach(
      ([categoryName, postCount]) => {
        const totalPages = postCount > 0 ? calculateTotalPages(postCount, postsPerPage) : 0;

        for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
          const categoryPath = `/${path}/${categoryName}/${pageNumber}`;
          const categoryUrl = generateLocalizedUrl({
            baseUrl,
            path: categoryPath,
            language,
          });
          const categoryPriority =
            categoryName === 'all'
              ? SITEMAP_PRIORITIES.ALL_CATEGORY
              : SITEMAP_PRIORITIES.CATEGORY;

          sitemap.push(
            buildSitemapEntry({
              url: categoryUrl,
              priority: categoryPriority,
            }),
          );
        }
      },
    );
  });

  return sitemap;
};

const generateCategorySitemapEntries = ({
  posts,
  baseUrl,
  path,
}: GeneratePagesProps): MetadataRoute.Sitemap => {
  const categoryPostCounts = aggregateCategoryPostCounts(posts);

  return generateCategoryPaginationSitemap({
    path: `${path}/category`,
    categoryPostCounts,
    baseUrl,
  });
};

// Author generator

const aggregateAuthorPostCounts = (
  posts: BlogNewsListItemProp[],
): LanguageCategoryCount => {
  const initialCount: LanguageCategoryCount = {
    ko: {},
    en: {},
  };

  posts.forEach(post => {
    if (!post.lang?.length) return;

    post.lang.forEach(language => {
      const normalizedLanguage = language.toLowerCase();
      if (!isSupportedLanguage(normalizedLanguage)) return;

      const currentCount = initialCount[normalizedLanguage][post.authorId] || 0;
      initialCount[normalizedLanguage][post.authorId] = currentCount + 1;
    });
  });

  return initialCount;
};

const generateAuthorPageEntries = ({
  posts,
  baseUrl,
  path,
}: GeneratePagesProps) => {
  const authorPostCounts = aggregateAuthorPostCounts(posts);
  const sitemap: MetadataRoute.Sitemap = [];

  SUPPORTED_LANGUAGES.forEach(language => {
    const authorCountsForLanguage = authorPostCounts[language];

    Object.entries(authorCountsForLanguage).forEach(([authorId, postCount]) => {
      const totalAuthorPages = calculateTotalPages(postCount);

      for (let pageNumber = 1; pageNumber <= totalAuthorPages; pageNumber++) {
        const authorPath = `/${path}/author/${authorId}/${pageNumber}`;
        const authorUrl = generateLocalizedUrl({
          baseUrl,
          path: authorPath,
          language,
        });

        sitemap.push(
          buildSitemapEntry({
            url: authorUrl,
            priority: SITEMAP_PRIORITIES.CATEGORY,
          }),
        );
      }
    });
  });

  return sitemap;
};

// Post generator

const generatePostEntries = ({ posts, baseUrl, path }: GeneratePagesProps) => {
  const sitemap: MetadataRoute.Sitemap = [];

  posts.forEach(post => {
    if (!post.slug || !post.lang?.length) return;

    const primaryLanguage = post.lang[0]?.toLowerCase();
    if (!isSupportedLanguage(primaryLanguage)) return;

    const blogPostUrl = generateLocalizedUrl({
      baseUrl,
      path: `/${path}/${post.slug}`,
      language: primaryLanguage,
    });
    const postLastModified = post.date ? new Date(post.date) : new Date();

    sitemap.push(
      buildSitemapEntry({
        url: blogPostUrl,
        lastModified: postLastModified,
        priority: SITEMAP_PRIORITIES.BLOG_POST,
      }),
    );
  });

  return sitemap;
};

// Content generator

export const createContentSitemap = async (
  id: ContentCategoryId,
  path: string,
  baseUrl: string,
): Promise<MetadataRoute.Sitemap> => {
  try {
    const posts = await getBlogNewsPostsForList(id);

    const categorySitemap = generateCategorySitemapEntries({
      posts,
      baseUrl,
      path,
    });
    const postSitemap = generatePostEntries({ posts, baseUrl, path });

    return [...categorySitemap, ...postSitemap];
  } catch (error) {
    console.error(`Error generating ${path} sitemap:`, error);

    return [
      buildSitemapEntry({
        url: `${baseUrl}/${path}/category/all/1`,
        priority: SITEMAP_PRIORITIES.ALL_CATEGORY,
      }),
      buildSitemapEntry({
        url: `${baseUrl}/ko/${path}/category/all/1`,
        priority: SITEMAP_PRIORITIES.ALL_CATEGORY,
      }),
    ];
  }
};

// Utility Functions

export const calculateTotalPages = (
  postCount: number,
  postsPerPage = 6,
): number => {
  return Math.ceil(postCount / postsPerPage) || 1;
};

interface BuildSitemapEntryProps {
  url: string;
  lastModified?: Date;
  priority?: number;
  changeFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  alternates?: {
    languages?: {
      ko?: string;
    };
  };
}

export const buildSitemapEntry = ({
  url,
  lastModified = new Date(),
  priority = 0.7,
  changeFrequency = 'daily',
  alternates,
}: BuildSitemapEntryProps): MetadataRoute.Sitemap[0] => {
  const entry: MetadataRoute.Sitemap[0] = {
    url,
    lastModified,
    changeFrequency,
    priority,
    alternates,
  };

  return entry;
};
